import { useCarrotCompanion } from "#/components/care-team/hooks/useCarrotCompanion"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useGetPhoneSupportNumberForUser } from "#/components/dynamic-content/hooks/useQueryPhoneSupport"
import { useSendMessagePath } from "#/components/talk-to-carrot/hooks/useSendMessagePath"
import { TwilioVoiceWidget } from "#/components/talk-to-carrot/VoiceWidget/TwilioVoiceWidget"
import { PhoneSupportNumber } from "#/lib/carrot-api/types/PhoneNumber"
import { Box, Button, Progress, Stack, Tile, Typography } from "@carrotfertility/carotene-core"
import React, { useState } from "react"
import { FormattedMessage } from "react-intl"
import { useHistory } from "react-router"
import { formatCarrotCompanionName } from "./utils/carrotCompanionNameUtil"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"

const CompanionAvatar = ({ photoUrl, initials }: { photoUrl?: string; initials: string }) =>
  photoUrl ? (
    <Box
      component="img"
      src={photoUrl}
      alt=""
      height="4.5rem"
      width="4.5rem"
      borderRadius={(theme) => theme.tokens.borderRadius.md}
    />
  ) : (
    <Box
      height="4.5rem"
      width="4.5rem"
      sx={{ backgroundColor: (theme) => theme.palette.warning.contrastText }}
      borderRadius={(theme) => theme.tokens.borderRadius.md}
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexShrink="0"
    >
      <Typography variant="h3" color={(theme) => theme.palette.secondary.contrastText}>
        {initials}
      </Typography>
    </Box>
  )

const CompanionDetails = ({ carrotCompanionName }: { carrotCompanionName: string }) => (
  <Stack gap={(theme) => theme.tokens.spacing.lg}>
    <Typography variant="h6">{carrotCompanionName}</Typography>

    <Typography>
      <FormattedMessage
        defaultMessage="{carrotCompanion} is a general expert on Carrot, including logistics, choosing a provider, and making the most of your benefit. If {carrotCompanion} is not available, another Carrot Companion will respond."
        values={{ carrotCompanion: carrotCompanionName }}
      />
    </Typography>
    <Typography variant="body1" fontStyle="italic">
      <FormattedMessage defaultMessage="Companions cannot answer medical questions" />
    </Typography>
  </Stack>
)

const PostCarrotPlanCarrotCompanionSection = ({
  carrotCompanion,
  initials,
  formattedName,
  handleClick
}: {
  carrotCompanion: { name: string; photoUrl?: string }
  initials: string
  formattedName: string
  handleClick: () => void
}) => (
  <Stack gap={(theme) => theme.tokens.spacing.sm}>
    <Stack gap={(theme) => theme.tokens.spacing.lg} direction="row" alignItems="end">
      <CompanionAvatar photoUrl={carrotCompanion.photoUrl} initials={initials} />
      <Button
        onClick={handleClick}
        color="secondary"
        variant="outlined"
        fullWidth={false}
        sx={{ inlineSize: "fit-content", blockSize: "fit-content" }}
        data-analytics-id="care-team-carrot-companion-send-message-button"
      >
        <FormattedMessage defaultMessage="Send a message" />
      </Button>
    </Stack>
    <CompanionDetails carrotCompanionName={formattedName} />
  </Stack>
)

const CarrotCompanionPreCpTile = () => {
  const carrotPlanRequestStatus = useGetCarrotPlanRequestStatus()
  return (
    <Tile color="primary" sx={{ padding: (theme) => theme.tokens.spacing.md }}>
      <Stack direction="row" gap={(theme) => theme.tokens.spacing.md} alignItems="center">
        <Box
          component="img"
          src="/images/care-navigators-2x.png"
          alt="Care Navigators"
          height="3.5rem"
          width="3.5rem"
        />
        <Typography>
          {carrotPlanRequestStatus.data?.carrotPlanIsOnTheWay ? (
            <FormattedMessage defaultMessage="Your Carrot Companion will be available when your Carrot Plan is ready." />
          ) : (
            <FormattedMessage defaultMessage="You don’t have a Carrot Companion yet. Get your Carrot Plan to be matched with a dedicated Carrot Companion." />
          )}
        </Typography>
      </Stack>
    </Tile>
  )
}

const CarrotCompanionPreCpDescription = ({
  shouldDisplayPhoneSupport,
  phoneNumber
}: {
  shouldDisplayPhoneSupport: boolean
  phoneNumber?: string
}) => (
  <>
    <Typography>
      <FormattedMessage defaultMessage="Carrot Companions are general experts on Carrot, including logistics, choosing a provider, and making the most of your benefit." />
    </Typography>
    <Typography fontStyle="italic">
      {shouldDisplayPhoneSupport ? (
        <FormattedMessage
          defaultMessage="Need help now? Talk to one of our Carrot Companions at {phoneNumber} or send us a message."
          values={{
            phoneNumber: phoneNumber
          }}
        />
      ) : (
        <FormattedMessage defaultMessage="Need help now? Talk to one of our Carrot Companions online." />
      )}
    </Typography>
    <Typography>
      <FormattedMessage defaultMessage="Carrot Companions cannot answer medical questions." />
    </Typography>
  </>
)

const PreCarrotPlanCarrotCompanionSection = ({
  shouldDisplayPhoneSupport,
  phoneNumber,
  onMessageClick
}: {
  shouldDisplayPhoneSupport: boolean
  phoneNumber?: PhoneSupportNumber
  onMessageClick: () => void
}) => {
  const [openFiveNineWidget, setOpenFiveNineWidget] = useState(false)
  const { mutate: trackAction } = useMemberActionTracking()

  const handleCallClick = () => {
    trackAction({
      memberActionTaken: MemberActions.CARE_TEAM_CALL_US_CLICKED
    })
    if (shouldDisplayPhoneSupport && phoneNumber) {
      window.open(`tel:${phoneNumber}`, "_self")
    } else {
      setOpenFiveNineWidget(true)
    }
  }

  return (
    <Stack gap={(theme) => theme.tokens.spacing.lg}>
      <CarrotCompanionPreCpTile />
      <CarrotCompanionPreCpDescription
        shouldDisplayPhoneSupport={shouldDisplayPhoneSupport}
        phoneNumber={phoneNumber?.internationalFormatPhoneNumber}
      />
      <Stack direction="row" gap={(theme) => theme.tokens.spacing.xs}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onMessageClick}
          fullWidth={false}
          sx={{ inlineSize: "fit-content" }}
          data-analytics-id="care-team-carrot-companion-message-care-team-button"
        >
          <FormattedMessage defaultMessage="Message Care Team" />
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCallClick}
          fullWidth={false}
          sx={{ inlineSize: "fit-content" }}
          data-analytics-id="care-team-carrot-companion-call-us-button"
        >
          <FormattedMessage defaultMessage="Call us" />
        </Button>
      </Stack>
      <TwilioVoiceWidget open={openFiveNineWidget} setOpen={setOpenFiveNineWidget} />
    </Stack>
  )
}

export const CarrotCompanionCareTeamSection = (): JSX.Element => {
  const history = useHistory()
  const { isInAppLockdown } = useCurrentUser()
  const { data: carrotCompanion, isLoading: isCarrotCompanionLoading } = useCarrotCompanion()
  const sendMessagePath = useSendMessagePath()
  const { data: phoneSupportNumber, isInitialLoading: isPhoneSupportNumberLoading } = useGetPhoneSupportNumberForUser()
  const shouldDisplayPhoneSupport = !isPhoneSupportNumberLoading && Boolean(phoneSupportNumber?.e164FormatPhoneNumber)
  const { mutate: trackAction } = useMemberActionTracking()

  const handleMessageButtonClick = () => {
    trackAction({
      memberActionTaken: MemberActions.CARE_TEAM_MESSAGE_CARE_TEAM_CLICKED,
      metaData: {
        isInAppLockdown
      }
    })
    history.push(sendMessagePath)
  }

  const { displayName, initials } = formatCarrotCompanionName(carrotCompanion?.name)
  if (isCarrotCompanionLoading && !isInAppLockdown) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Progress />
      </Box>
    )
  }

  return (
    <Stack gap={(theme) => theme.tokens.spacing.lg}>
      <Typography variant="h3">
        <FormattedMessage defaultMessage="Carrot Companion" />
      </Typography>
      {!isInAppLockdown && !isCarrotCompanionLoading && carrotCompanion ? (
        <PostCarrotPlanCarrotCompanionSection
          carrotCompanion={carrotCompanion}
          initials={initials}
          formattedName={displayName}
          handleClick={handleMessageButtonClick}
        />
      ) : (
        <PreCarrotPlanCarrotCompanionSection
          shouldDisplayPhoneSupport={shouldDisplayPhoneSupport}
          phoneNumber={phoneSupportNumber}
          onMessageClick={handleMessageButtonClick}
        />
      )}
    </Stack>
  )
}
